import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ChatMessage from "../ChatMessage/ChatMessage";
import ConversationBar from "../ConversationBar/ConversationBar";
import ContentCanvas from "../ContentCanvas/ContentCanvas";
import {
    getRepliPostHistory,
    clearRepliPostHistory,
    sendRepliMessage,
    sendRepliMessageStream,
    sendRepliFileMessage
} from "../../api/chatService";
import { translations } from "../../utils/translations";
import "./RepliPostSocialAssistant.css";

const RepliPostSocialAssistant = ({ user }) => {
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);
    const [chatHistory, setChatHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showBlogPrompt, setShowBlogPrompt] = useState(false);
    const [includeBlog, setIncludeBlog] = useState(false);
    const [generatedContent, setGeneratedContent] = useState({ post: "", blog: null });
    const [isCanvasOpen, setIsCanvasOpen] = useState(false);
    const chatContainerRef = useRef(null);
    const windowRef = useRef(null);
    const abortControllerRef = useRef(null);

    const location = useLocation();
    const currentLang = localStorage.getItem("language") || "es";
    const t = translations[currentLang]?.social || translations["es"].social;
    const tHome = translations[currentLang]?.home || translations["es"].home;
    const tProfile = translations[currentLang]?.profile || translations["es"].profile;

    // Show only on /social routes and for owners with linked repliker
    const canShow = !!(user && user.isOwner && user.linkedReplikerURL);
    const isSocialRoute = location.pathname.startsWith('/social') ||
        location.pathname === '/explore' ||
        location.pathname === '/profile' ||
        location.pathname === '/account';

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTooltip(false);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    // Use a ref to track the last loaded user ID to avoid redundant fetches
    const lastUserIdRef = useRef(user?.id);

    useEffect(() => {
        const initConversation = async () => {
            if (canShow && isSocialRoute) {
                // Only fetch if history is empty or user changed
                if (chatHistory.length > 0 && lastUserIdRef.current === user?.id) {
                    return;
                }

                try {
                    const data = await getRepliPostHistory();
                    if (data && data.history) {
                        const cleanHistory = data.history.filter(msg => {
                            const textContent = msg.text || "";
                            return !textContent.includes("El aumento del teletrabajo.pdf");
                        });
                        setChatHistory(cleanHistory);
                        lastUserIdRef.current = user?.id;

                        // Auto-open Canvas if the last message in history is a draft
                        const lastMsg = cleanHistory.length > 0 ? cleanHistory[cleanHistory.length - 1] : null;
                        if (lastMsg && lastMsg.sender === "bot" && lastMsg.text) {
                            detectAndOpenCanvas(lastMsg.text);
                        }
                    }
                } catch (error) {
                    console.error("Error initializing conversation:", error);
                }
            }
        };

        if (isChatVisible && canShow) {
            initConversation();
        }
    }, [isChatVisible, isSocialRoute, canShow, user?.id, chatHistory.length]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory, isChatVisible]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (windowRef.current && !windowRef.current.contains(event.target)) {
                setIsChatVisible(false);
            }
        };

        if (isChatVisible) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isChatVisible]);

    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
    };

    /**
     * Robustly detects JSON drafts in text and opens the Canvas
     */
    const detectAndOpenCanvas = (text) => {
        console.log("🔍 [Canvas] Analizando mensaje para detectar JSON...");
        if (!text || (!text.includes("{") && !text.includes("}"))) {
            console.log("❌ [Canvas] No se encontraron llaves { }.");
            return false;
        }

        try {
            const startIndex = text.indexOf('{');
            const endIndex = text.lastIndexOf('}');
            console.log("📍 [Canvas] Buscando entre índices:", startIndex, endIndex);

            if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
                let jsonStr = text.substring(startIndex, endIndex + 1);
                console.log("📦 [Canvas] Bloque detectado (pre-limpieza):", jsonStr);

                // Clean potential markdown or prefix text
                jsonStr = jsonStr.replace(/```json/g, '').replace(/```/g, '').trim();

                try {
                    const parsed = JSON.parse(jsonStr);
                    console.log("✅ [Canvas] JSON parseado con éxito:", parsed);

                    if (parsed.post || (parsed.blog && (parsed.blog.title || parsed.blog.content))) {
                        console.log("🚀 [Canvas] Borrador válido. Abriendo interface...");
                        setGeneratedContent({
                            post: parsed.post || "",
                            blog: parsed.blog || null
                        });
                        setIsCanvasOpen(true);
                        return true;
                    } else {
                        console.log("⚠️ [Canvas] JSON encontrado pero campos clave vacíos.");
                    }
                } catch (parseErr) {
                    console.log("⚠️ [Canvas] JSON.parse falló, intentando Regex...");
                    const postMatch = jsonStr.match(/"post":\s*"([\s\S]*?)"/);
                    const titleMatch = jsonStr.match(/"title":\s*"([\s\S]*?)"/);
                    const contentMatch = jsonStr.match(/"content":\s*"([\s\S]*?)"/);
                    const imageMatch = jsonStr.match(/"imageUrl":\s*"([\s\S]*?)"/);

                    if (postMatch || titleMatch || contentMatch) {
                        console.log("💡 [Canvas] Recuperado vía Regex manual.");
                        setGeneratedContent({
                            post: postMatch ? postMatch[1].replace(/\\n/g, "\n") : "",
                            blog: {
                                title: titleMatch ? titleMatch[1] : "Borrador de Blog",
                                content: contentMatch ? contentMatch[1].replace(/\\n/g, "\n") : "",
                                imageUrl: imageMatch ? imageMatch[1] : ""
                            }
                        });
                        setIsCanvasOpen(true);
                        return true;
                    }
                }
            } else {
                console.log("❌ [Canvas] Índices de llaves no válidos para extracción.");
            }
        } catch (err) {
            console.error("🔥 [Canvas] Error detectando JSON:", err);
        }
        return false;
    };

    const handleNewUserMessage = async (messageObject) => {
        const { text, file } = messageObject;
        if (!text.trim() && !file) return;

        setShowBlogPrompt(false);
        setChatHistory((prev) => [
            ...prev,
            messageObject,
            { sender: "bot", text: "", thinking: true, streamStatus: "Analizando..." }
        ]);
        setIsLoading(true);

        const finalText = text;

        try {
            if (file) {
                const response = await sendRepliFileMessage(file, finalText, "repli-post");
                if (response && response.history) {
                    setChatHistory(response.history);
                    const lastBotMsg = response.history.slice().reverse().find(m => m.sender === "bot");
                    if (lastBotMsg?.text) detectAndOpenCanvas(lastBotMsg.text);
                } else {
                    const botText = response?.message || "";
                    const botMessage = { sender: "bot", text: botText };
                    setChatHistory((prev) => {
                        const newHistory = prev.filter(m => !m.thinking);
                        return [...newHistory, botMessage];
                    });
                    detectAndOpenCanvas(botText);
                }
            } else {
                // Modo Streaming para mensajes de texto
                await sendRepliMessageStream(finalText, "repli-post", {
                    onToken: (token) => {
                        setChatHistory((prev) => {
                            const prevChat = [...prev];
                            const lastMsg = { ...prevChat[prevChat.length - 1] };
                            if (lastMsg.sender === "bot") {
                                lastMsg.thinking = false;
                                lastMsg.streamStatus = null;
                                lastMsg.text = (lastMsg.text || "") + token;
                                prevChat[prevChat.length - 1] = lastMsg;
                            }
                            return prevChat;
                        });
                    },
                    onStatus: (status) => {
                        setChatHistory((prev) => {
                            const prevChat = [...prev];
                            const lastMsg = { ...prevChat[prevChat.length - 1] };
                            if (lastMsg.sender === "bot" && lastMsg.thinking) {
                                lastMsg.streamStatus = status;
                                prevChat[prevChat.length - 1] = lastMsg;
                            }
                            return prevChat;
                        });
                    },
                    onDone: (data) => {
                        if (data && data.history) {
                            setChatHistory(data.history);
                            const botText = data.message || "";
                            detectAndOpenCanvas(botText);
                        }
                    },
                    onError: (err) => {
                        console.error("Stream error in RepliPost Assistant:", err);
                        throw err;
                    }
                });
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                setChatHistory((prev) => prev.filter(m => !m.thinking));
            } else {
                const errorMessage = { sender: "bot", text: t.errorConnect || "Error de conexión" };
                setChatHistory((prev) => [...prev.filter(m => !m.thinking), errorMessage]);
            }
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    };

    const handleGenerateBlog = () => {
        setShowBlogPrompt(false);

        if (!generatedContent.blog) {
            const lastBotMsg = chatHistory.slice().reverse().find(m => m.sender === "bot");
            if (lastBotMsg && lastBotMsg.text) {
                const text = lastBotMsg.text;
                setGeneratedContent({
                    post: "Post sobre el tema discutido",
                    blog: {
                        title: "Artículo generado",
                        content: text.substring(0, 2000),
                        imageUrl: ""
                    }
                });
            }
        }
        setIsCanvasOpen(true);
    };

    const handleSkipBlog = () => {
        setShowBlogPrompt(false);
        setIsCanvasOpen(true);
    };

    const handleManualPublishBlog = () => {
        const lastBotMsg = chatHistory.slice().reverse().find(m => m.sender === "bot");
        if (lastBotMsg && lastBotMsg.text) {
            const text = lastBotMsg.text;
            setGeneratedContent({
                post: "Post sobre el tema discutido",
                blog: {
                    title: "Artículo desde el chat",
                    content: text.substring(0, 5000),
                    imageUrl: ""
                }
            });
        }
        setIsCanvasOpen(true);
    };

    const handleClearChat = async () => {
        if (window.confirm(t.confirmClearChat || "¿Estás seguro de que quieres limpiar la conversación?")) {
            try {
                const response = await clearRepliPostHistory();
                if (response && response.history) {
                    setChatHistory(response.history);
                } else {
                    setChatHistory([]);
                }
            } catch (error) {
                console.error("Error clearing repli post chat history:", error);
            }
        }
    };

    if (!isSocialRoute || !canShow) return null;

    return (
        <div className={`repli-post-assistant-floating ${isChatVisible ? "active" : ""} ${isCanvasOpen ? "canvas-open" : ""}`}>
            <div className="assistant-content-wrapper">
                <ContentCanvas
                    isOpen={isCanvasOpen}
                    onClose={() => setIsCanvasOpen(false)}
                    postContent={generatedContent.post}
                    blogData={generatedContent.blog}
                    translations={tHome}
                    onPostPublished={() => console.log("Post published")}
                    onBlogPublished={() => console.log("Blog published")}
                />

                {isChatVisible && (
                    <div className="floating-chat-window" ref={windowRef}>
                        <div className="floating-chat-header">
                            <div className="header-left">
                                <img src="/InsightRepli.ico" alt="Repliker" className="repli-mini-icon-img" />
                                <span>Repli Post</span>
                            </div>
                            <div className="header-right">
                                <button className="clean-btn" onClick={handleClearChat} title="Limpiar">
                                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T720-120H280Zm440-600H240v520q0 8 6 14t14 6h440q8 0 14-6t6-14v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM240-720v520-520Z" /></svg>
                                </button>
                                <button className="close-btn" onClick={() => setIsChatVisible(false)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                                </button>
                            </div>
                        </div>
                        <div className="floating-chat-messages" ref={chatContainerRef}>
                            {chatHistory.length === 0 && (
                                <div className="empty-chat-msg">{tProfile.addContent || "Añade contenido"}</div>
                            )}
                            {chatHistory
                                .filter(msg => msg && msg.sender)
                                .map((msg, index) => (
                                    <div key={index} className={`message-group ${msg.sender === 'bot' ? 'bot' : 'user'}`}>
                                        <ChatMessage
                                            sender={msg.sender}
                                            text={msg.text}
                                            file={msg.file}
                                            thinking={msg.thinking}
                                            status={msg.streamStatus}
                                        />
                                        {msg.sender === "bot" && (msg.text?.includes("{") || msg.text?.includes('"blog"')) && (
                                            <button
                                                className="manual-canvas-trigger-btn"
                                                onClick={() => detectAndOpenCanvas(msg.text)}
                                            >
                                                Abrir en Editor Lateral
                                            </button>
                                        )}
                                    </div>
                                ))}
                            {isLoading && !chatHistory.some(m => m.thinking) && (
                                <div className="bot-message thinking-bubble">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            )}
                            {showBlogPrompt && (
                                <div className="blog-prompt-message">
                                    <p>{tHome.repliGenerateBlog || "¿Quieres que también te genere un blog SEO para tu web?"}</p>
                                    <div className="blog-prompt-buttons">
                                        <button
                                            className="blog-prompt-yes"
                                            onClick={handleGenerateBlog}
                                        >
                                            {tHome.repliGenerateBlogYes || "¡Sí, generar blog!"}
                                        </button>
                                        <button
                                            className="blog-prompt-no"
                                            onClick={handleSkipBlog}
                                        >
                                            {tHome.repliGenerateBlogNo || "Solo post"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {chatHistory.length > 0 && chatHistory[chatHistory.length - 1]?.sender === "bot" && (
                                <button
                                    className="publish-blog-btn"
                                    onClick={handleManualPublishBlog}
                                    style={{
                                        margin: "10px",
                                        padding: "6px 14px",
                                        backgroundColor: "rgba(153, 0, 255, 0.08)",
                                        color: "#b366ff",
                                        border: "1px solid rgba(153, 0, 255, 0.25)",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        fontSize: "0.78rem",
                                        fontWeight: "500",
                                        letterSpacing: "0.2px",
                                        transition: "background-color 0.15s ease, color 0.15s ease"
                                    }}
                                >
                                    📄 Abrir en Editor Lateral
                                </button>
                            )}
                        </div>
                        <div className="floating-chat-messages-bottom-gradient"></div>
                        <div className="floating-chat-footer">
                            <div className="blog-toggle-row">
                                <label className="blog-toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={includeBlog}
                                        onChange={(e) => setIncludeBlog(e.target.checked)}
                                    />
                                    <span className="toggle-text">
                                        {tHome.repliGenerateBlog || "Incluir blog SEO"}
                                    </span>
                                </label>
                            </div>
                            <ConversationBar
                                onNewUserMessage={handleNewUserMessage}
                                onCancel={handleCancel}
                                isLoading={isLoading}
                                selectedFile={selectedFile}
                                setSelectedFile={setSelectedFile}
                                placeholder={t.searchPlaceholder || "Escribe aquí..."}
                            />
                        </div>
                    </div>
                )}
            </div>

            {!isChatVisible && (
                <button
                    className="floating-trigger-btn"
                    onClick={() => setIsChatVisible(true)}
                    title="Crear contenido con Repli"
                >
                    {showTooltip && (
                        <div className="repli-post-tooltip">
                            Dime qué quieres publicar
                        </div>
                    )}
                    <img src="/InsightRepli.ico" alt="Repli Post" className="trigger-icon-img" />
                    <span className="notification-dot"></span>
                </button>
            )}

        </div>
    );
};

export default RepliPostSocialAssistant;
